import React from 'react'
import { Tooltip, Box, Truncate } from '@sketch/components'
import {
  routes,
  useHandleCopyValue,
  CopiableArea,
} from '@sketch/modules-common'
import { roundUpTo } from '@sketch/utils'
import {
  AttributeList,
  Attribute,
  AttributeLabel,
  AttributeValue,
} from '../../Attribute/Attribute'
import { PopoverHeader } from '../../../../PopoverHeader'
import { FormattedColorValue } from '../FormattedColorValue'
import { ColorFormat } from '../../../../../../types'
import {
  ClickableArea,
  ColorInfo,
  ColorNameInPopover,
  ColorPathInPopover,
  LinkStyled,
  VersionLinkStyled,
  ColorSwatch,
} from './ColorVariablePopover.styles'
import { rgbTo } from '../utils'
import { useVersioning } from '../../../../../../../versioning'

const LinkUnavailable: React.FCC = ({ children }) => (
  <Tooltip
    placement="left"
    content="This color belongs to a Library that is unavailable"
  >
    {children}
  </Tooltip>
)

const ColorNamePath = ({ name, path }: { name: string; path: string }) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(name)

  return (
    <ColorInfo>
      <Tooltip
        placement="left"
        spacing="10px"
        visible={copyTooltipVisible}
        content={copyTooltipText}
      >
        <ClickableArea
          onClick={handleCopyValue}
          onMouseEnter={handleEnterCopiableArea}
          onMouseLeave={handleLeaveCopiableArea}
        >
          <ColorNameInPopover>{name}</ColorNameInPopover>
        </ClickableArea>
      </Tooltip>
      <ColorPathInPopover>{path}</ColorPathInPopover>
    </ColorInfo>
  )
}

const ColorAtribute = ({
  label,
  value,
  copyValue,
}: {
  label: string
  value: string | React.ReactNode
  copyValue?: string
}) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue ?? `${value}`)

  return (
    <Tooltip
      placement="left"
      spacing="10px"
      visible={copyTooltipVisible}
      content={copyTooltipText}
    >
      <CopiableArea
        onClick={handleCopyValue}
        onMouseEnter={handleEnterCopiableArea}
        onMouseLeave={handleLeaveCopiableArea}
      >
        <Attribute noCopy>
          <AttributeLabel>{label}</AttributeLabel>
          <AttributeValue valueString={copyValue} label={label}>
            <Truncate>{value}</Truncate>
          </AttributeValue>
        </Attribute>
      </CopiableArea>
    </Tooltip>
  )
}

interface PopoverContentProps {
  color: { red: number; green: number; blue: number; alpha?: number }
  name: string
  path: string
  shareID?: string
  componentUnavailable?: boolean
  searchParams?: string
  isForeign?: boolean
  handleClose: () => void
}

export const PopoverContent: React.FCC<PopoverContentProps> = ({
  color,
  shareID,
  componentUnavailable,
  searchParams,
  isForeign,
  name,
  path,
  handleClose,
}) => {
  const versionContext = useVersioning()
  const { share: shareFromContext } = versionContext

  const linkExternalComponent = componentUnavailable ? (
    <LinkUnavailable>
      <ColorNamePath name={name} path={path} />
    </LinkUnavailable>
  ) : (
    <LinkStyled
      target="_blank"
      to={{
        pathname: routes.CWV_COLOR_VARIABLES.create({
          shareID: shareID!,
        }),
        search: searchParams,
      }}
    >
      <ColorNamePath name={name} path={path} />
    </LinkStyled>
  )

  const linkInternalComponent = (
    <VersionLinkStyled
      target="_blank"
      to={{
        routeKey: 'CWV_COLOR_VARIABLES',
        routeParams: {
          shareID: shareFromContext.identifier,
        },
        search: searchParams,
      }}
    >
      <ColorNamePath name={name} path={path} />
    </VersionLinkStyled>
  )

  // Note: when linking to a foreign component we always link to the latest
  // version, but for internal components we want a versioned link
  const wrapperLink = isForeign ? linkExternalComponent : linkInternalComponent

  const roundedAlpha = roundUpTo(color.alpha || 1, 2)
  const hex = rgbTo(ColorFormat.HEX, color)
  const hsl = rgbTo(ColorFormat.HSL, color)
  const rgbaString = `rgba(${color.red},${color.green},${color.blue},${roundedAlpha})`

  return (
    <Box width={300}>
      <PopoverHeader onClose={handleClose} title={wrapperLink} />
      <ColorSwatch
        red={color.red}
        green={color.green}
        blue={color.blue}
        alpha={color.alpha}
      />
      <Box pr={5} pl={5} pb={2}>
        <AttributeList>
          <ColorAtribute
            label="Hex"
            copyValue={hex}
            value={
              <FormattedColorValue format={ColorFormat.HEX} color={color} />
            }
          />
          <ColorAtribute
            label="RGB"
            copyValue={rgbaString}
            value={
              <FormattedColorValue format={ColorFormat.RGB} color={color} />
            }
          />
          <ColorAtribute
            label="HSL"
            copyValue={hsl}
            value={
              <FormattedColorValue format={ColorFormat.HSL} color={color} />
            }
          />
          <ColorAtribute
            label="NSColor Objective-C"
            value={
              <FormattedColorValue
                format={ColorFormat.OBJC_NSCOLOR}
                color={color}
              />
            }
          />
          <ColorAtribute
            label="NSColor Swift"
            value={
              <FormattedColorValue
                format={ColorFormat.SWIFT_NSCOLOR}
                color={color}
              />
            }
          />
          <ColorAtribute
            label="UIColor Objective-C"
            value={
              <FormattedColorValue
                format={ColorFormat.OBJC_UICOLOR}
                color={color}
              />
            }
          />
          <ColorAtribute
            label="UIColor Swift"
            value={
              <FormattedColorValue
                format={ColorFormat.SWIFT_UICOLOR}
                color={color}
              />
            }
          />
        </AttributeList>
      </Box>
    </Box>
  )
}
